import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Form, Modal, Spinner } from '@/components/ui';
import { useAuth } from '../../../contexts/AuthContext';
import { usePermissions } from '../../../hooks/usePermissions';
import messageBoardService, {
  type MessageBoardPersonOption,
  type MessageBoardPostCreated,
  type MessageBoardPostUrgency,
  type MessageBoardServiceAreaOption,
} from '../../../services/messageBoardService';

export interface MessageBoardComposerProps {
  /** Optional callback after a successful create (e.g. MB-03 feed prepend). */
  onPosted?: (post: MessageBoardPostCreated) => void;
}

type ToKind = 'foreman_area' | 'service_area' | 'people' | 'digital_sign';

interface ForemanAreaOption {
  id: number;
  name: string;
  serviceAreaId?: number;
}

const MessageBoardComposer: React.FC<MessageBoardComposerProps> = ({ onPosted }) => {
  const { currentUser } = useAuth();
  const { hasPermission, loading: permissionsLoading } = usePermissions();
  const canPost = hasPermission('message_board:post');

  const foremanAreas = (currentUser?.foremanAreas ?? []) as ForemanAreaOption[];
  const defaultAreaId =
    (currentUser as { defaultForemanAreaId?: number } | null)?.defaultForemanAreaId ??
    foremanAreas[0]?.id ??
    null;

  const [toKind, setToKind] = useState<ToKind>('foreman_area');
  const [selectedForemanAreaIds, setSelectedForemanAreaIds] = useState<number[]>(
    defaultAreaId != null ? [defaultAreaId] : []
  );
  const [areaSearch, setAreaSearch] = useState('');
  const [serviceAreas, setServiceAreas] = useState<MessageBoardServiceAreaOption[]>([]);
  const [serviceAreasLoading, setServiceAreasLoading] = useState(false);
  const [serviceAreasError, setServiceAreasError] = useState<string | null>(null);
  const [selectedServiceAreaId, setSelectedServiceAreaId] = useState<number | null>(null);
  const [peopleAreaId, setPeopleAreaId] = useState<number | ''>(defaultAreaId ?? '');
  const [people, setPeople] = useState<MessageBoardPersonOption[]>([]);
  const [peopleLoading, setPeopleLoading] = useState(false);
  const [peopleError, setPeopleError] = useState<string | null>(null);
  const [peopleSearch, setPeopleSearch] = useState('');
  const [selectedPersonIds, setSelectedPersonIds] = useState<number[]>([]);
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [urgency, setUrgency] = useState<MessageBoardPostUrgency>('NORMAL');
  const [requireAck, setRequireAck] = useState(false);
  const [messageError, setMessageError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [successInfo, setSuccessInfo] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [showUrgentConfirm, setShowUrgentConfirm] = useState(false);

  useEffect(() => {
    if (selectedForemanAreaIds.length === 0 && defaultAreaId != null) {
      setSelectedForemanAreaIds([defaultAreaId]);
    }
    if (peopleAreaId === '' && defaultAreaId != null) {
      setPeopleAreaId(defaultAreaId);
    }
  }, [defaultAreaId, peopleAreaId, selectedForemanAreaIds.length]);

  useEffect(() => {
    if (toKind !== 'service_area') return;
    let cancelled = false;
    setServiceAreasLoading(true);
    setServiceAreasError(null);
    messageBoardService
      .listAudience()
      .then((result) => {
        if (cancelled) return;
        setServiceAreas(result.serviceAreas);
        setSelectedServiceAreaId((current) => {
          if (current != null && result.serviceAreas.some((area) => area.id === current)) {
            return current;
          }
          return result.serviceAreas[0]?.id ?? null;
        });
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setServiceAreasError(err instanceof Error ? err.message : 'Failed to load service areas');
        }
      })
      .finally(() => {
        if (!cancelled) setServiceAreasLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [toKind]);

  useEffect(() => {
    if (toKind !== 'people' || typeof peopleAreaId !== 'number') {
      setPeople([]);
      return;
    }
    let cancelled = false;
    setPeopleLoading(true);
    setPeopleError(null);
    setSelectedPersonIds([]);
    messageBoardService
      .listPeople(peopleAreaId)
      .then((result) => {
        if (!cancelled) setPeople(result.people);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setPeople([]);
          setPeopleError(err instanceof Error ? err.message : 'Failed to load people');
        }
      })
      .finally(() => {
        if (!cancelled) setPeopleLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [toKind, peopleAreaId]);

  const filteredAreas = useMemo(() => {
    const q = areaSearch.trim().toLowerCase();
    if (!q) return foremanAreas;
    return foremanAreas.filter((area) => area.name.toLowerCase().includes(q));
  }, [areaSearch, foremanAreas]);

  const allForemanAreasSelected =
    foremanAreas.length > 0 && selectedForemanAreaIds.length === foremanAreas.length;

  const selectedServiceArea = serviceAreas.find((area) => area.id === selectedServiceAreaId) ?? null;

  const targetForemanAreaIds = useMemo((): number[] => {
    if (toKind === 'foreman_area') return selectedForemanAreaIds;
    if (toKind === 'service_area') return selectedServiceArea?.foremanAreaIds ?? [];
    return [];
  }, [toKind, selectedForemanAreaIds, selectedServiceArea]);

  const targetAreaNames = targetForemanAreaIds
    .map((id) => foremanAreas.find((area) => area.id === id)?.name)
    .filter((name): name is string => !!name);

  const filteredPeople = useMemo(() => {
    const q = peopleSearch.trim().toLowerCase();
    if (!q) return people;
    return people.filter((person) => person.name.toLowerCase().includes(q));
  }, [people, peopleSearch]);

  const canSendPeople = toKind === 'people' && typeof peopleAreaId === 'number' && selectedPersonIds.length > 0;
  const canSend =
    canSendPeople ||
    ((toKind === 'foreman_area' || toKind === 'service_area') && targetForemanAreaIds.length > 0);

  const audienceLabel = useMemo(() => {
    if (toKind === 'digital_sign') return 'Digital sign board';
    if (toKind === 'people') {
      const areaName = foremanAreas.find((area) => area.id === peopleAreaId)?.name ?? 'an area';
      if (selectedPersonIds.length === 0) return `People in ${areaName}`;
      return `${selectedPersonIds.length} ${selectedPersonIds.length === 1 ? 'person' : 'people'} in ${areaName}`;
    }
    if (toKind === 'service_area') return selectedServiceArea?.name ?? 'a service area';
    if (targetAreaNames.length === 0) return 'a foreman area';
    if (targetAreaNames.length === foremanAreas.length && foremanAreas.length > 1) return 'all your foreman areas';
    if (targetAreaNames.length === 1) return targetAreaNames[0];
    return `${targetAreaNames.length} foreman areas`;
  }, [
    toKind,
    peopleAreaId,
    selectedPersonIds.length,
    foremanAreas,
    selectedServiceArea,
    targetAreaNames,
  ]);

  if (permissionsLoading) {
    return (
      <div className="tw-flex tw-items-center tw-gap-2 tw-text-content-secondary">
        <Spinner size="sm" />
        <span>Checking permissions…</span>
      </div>
    );
  }

  if (!canPost) {
    return (
      <Alert variant="warning">
        You do not have permission to post to the message center.
      </Alert>
    );
  }

  if (foremanAreas.length === 0) {
    return (
      <Alert variant="warning">
        You have no foreman areas assigned, so there is nowhere to send a notification.
      </Alert>
    );
  }

  const resetForm = () => {
    setToKind('foreman_area');
    setSubject('');
    setMessage('');
    setUrgency('NORMAL');
    setRequireAck(false);
    setMessageError(null);
    setSelectedForemanAreaIds(defaultAreaId != null ? [defaultAreaId] : []);
    setSelectedPersonIds([]);
    setPeopleSearch('');
    setAreaSearch('');
  };

  const buildMessageBody = (): string => {
    const body = message.trim();
    const sub = subject.trim();
    if (!sub) return body;
    return `${sub}\n\n${body}`;
  };

  const validate = (): boolean => {
    if (!message.trim()) {
      setMessageError('Message body is required');
      return false;
    }
    if (buildMessageBody().length > 2000) {
      setMessageError('Message must be 2000 characters or fewer');
      return false;
    }
    setMessageError(null);
    if (toKind === 'digital_sign') {
      setSubmitError('Digital sign board delivery is not available yet.');
      return false;
    }
    if (toKind === 'people' && selectedPersonIds.length === 0) {
      setSubmitError('Choose at least one person.');
      return false;
    }
    if (!canSend) {
      setSubmitError('Choose at least one area you have access to.');
      return false;
    }
    return true;
  };

  const post = async () => {
    if (!validate()) return;
    if (toKind !== 'people' && targetForemanAreaIds.length === 0) return;
    if (toKind === 'people' && typeof peopleAreaId !== 'number') return;

    setSubmitting(true);
    setSubmitError(null);
    setSuccessInfo(null);
    try {
      const body = buildMessageBody();
      if (toKind === 'people' && typeof peopleAreaId === 'number') {
        const created = await messageBoardService.create({
          message: body,
          foremanAreaId: peopleAreaId,
          urgency,
          recipientUserIds: selectedPersonIds,
        });
        resetForm();
        setSuccessInfo(
          `Sent to ${created.recipientCount} ${created.recipientCount === 1 ? 'person' : 'people'} · ${audienceLabel}`
        );
        onPosted?.(created);
        return;
      }
      let recipientTotal = 0;
      for (const foremanAreaId of targetForemanAreaIds) {
        const created = await messageBoardService.create({
          message: body,
          foremanAreaId,
          urgency,
        });
        recipientTotal += created.recipientCount;
        onPosted?.(created);
      }
      resetForm();
      const areaWord = targetForemanAreaIds.length === 1 ? 'area' : 'areas';
      setSuccessInfo(
        `Sent to ${recipientTotal} ${recipientTotal === 1 ? 'person' : 'people'} across ${targetForemanAreaIds.length} ${areaWord} · ${audienceLabel}`
      );
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to send notification');
    } finally {
      setSubmitting(false);
      setShowUrgentConfirm(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    if (urgency === 'URGENT') {
      setShowUrgentConfirm(true);
      return;
    }
    void post();
  };

  const toggleForemanArea = (id: number) => {
    setSelectedForemanAreaIds((current) =>
      current.includes(id) ? current.filter((value) => value !== id) : [...current, id]
    );
  };

  const selectAllForemanAreas = () => {
    setSelectedForemanAreaIds(foremanAreas.map((area) => area.id));
  };

  const toPillClass = (active: boolean) =>
    `tw-inline-flex tw-min-h-11 tw-items-center tw-rounded-full tw-border tw-px-3.5 tw-py-2 tw-text-sm tw-font-medium tw-transition-colors ${
      active
        ? 'tw-border-ink tw-bg-ink tw-text-white'
        : 'tw-border-line-input tw-bg-surface tw-text-content hover:tw-border-ink/30 hover:tw-bg-surface-alt'
    }`;

  const optionChipClass = (active: boolean) =>
    `tw-inline-flex tw-min-h-11 tw-items-center tw-gap-1.5 tw-rounded-full tw-border tw-px-3.5 tw-py-2 tw-text-sm tw-font-medium tw-transition-colors ${
      active
        ? 'tw-border-ink tw-bg-ink tw-text-white'
        : 'tw-border-line-input tw-bg-surface tw-text-content hover:tw-border-ink/30 hover:tw-bg-surface-alt'
    }`;

  const sendDisabled = submitting || !canSend;
  const confirmAreaLabel =
    targetAreaNames.length === 0
      ? audienceLabel
      : targetAreaNames.length === 1
        ? targetAreaNames[0]
        : targetAreaNames.join(', ');

  return (
    <>
      <form
        onSubmit={handleSubmit}
        className="tw-overflow-hidden tw-rounded-xl tw-border tw-border-line-input tw-bg-surface tw-shadow-card"
        noValidate
      >
        <div className="tw-p-4 sm:tw-p-5">
          <p className="tw-mb-4 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-content-muted">
            Send a notification
          </p>

          {successInfo && (
            <Alert
              variant="success"
              className="tw-mb-3"
              dismissible
              onClose={() => setSuccessInfo(null)}
            >
              {successInfo}
            </Alert>
          )}
          {submitError && (
            <Alert
              variant="danger"
              className="tw-mb-3"
              dismissible
              onClose={() => setSubmitError(null)}
            >
              {submitError}
            </Alert>
          )}

          <div className="tw-mb-4">
            <p className="tw-mb-2 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-content-muted">
              To
            </p>
            <div
              role="group"
              aria-label="Notification recipients"
              className="tw-flex tw-flex-wrap tw-gap-2"
            >
              <button
                type="button"
                className={toPillClass(toKind === 'foreman_area')}
                aria-pressed={toKind === 'foreman_area'}
                onClick={() => setToKind('foreman_area')}
              >
                Foreman area
              </button>
              <button
                type="button"
                className={toPillClass(toKind === 'service_area')}
                aria-pressed={toKind === 'service_area'}
                onClick={() => setToKind('service_area')}
              >
                Service area
              </button>
              <button
                type="button"
                className={toPillClass(toKind === 'people')}
                aria-pressed={toKind === 'people'}
                onClick={() => setToKind('people')}
              >
                People
              </button>
              <button
                type="button"
                className={toPillClass(toKind === 'digital_sign')}
                aria-pressed={toKind === 'digital_sign'}
                onClick={() => setToKind('digital_sign')}
              >
                Digital sign board
              </button>
            </div>

            {toKind === 'foreman_area' && (
              <div className="tw-mt-3 tw-overflow-hidden tw-rounded-lg tw-border tw-border-line-input tw-bg-surface">
                <div className="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-border-b tw-border-line tw-px-3 tw-py-2">
                  <p className="tw-m-0 tw-text-sm tw-text-content-secondary">
                    {selectedForemanAreaIds.length} selected
                  </p>
                  <button
                    type="button"
                    className="tw-min-h-11 tw-text-sm tw-font-medium tw-text-primary hover:tw-underline"
                    onClick={selectAllForemanAreas}
                    disabled={allForemanAreasSelected}
                  >
                    All my areas
                  </button>
                </div>
                <div className="tw-relative tw-border-b tw-border-line">
                  <i
                    className="bi bi-search tw-pointer-events-none tw-absolute tw-left-3 tw-top-1/2 tw--translate-y-1/2 tw-text-content-muted"
                    aria-hidden
                  />
                  <input
                    id="message-board-area-search"
                    type="search"
                    value={areaSearch}
                    onChange={(e) => setAreaSearch(e.target.value)}
                    placeholder="Search foreman areas..."
                    className="tw-block tw-w-full tw-border-0 tw-bg-transparent tw-py-2.5 tw-pl-9 tw-pr-3 tw-text-sm tw-text-content placeholder:tw-text-content-muted focus:tw-outline-none"
                    aria-label="Search foreman areas"
                  />
                </div>
                <ul
                  role="listbox"
                  aria-label="Foreman areas"
                  aria-multiselectable="true"
                  className="tw-m-0 tw-max-h-48 tw-list-none tw-overflow-y-auto tw-p-0"
                >
                  {filteredAreas.length === 0 ? (
                    <li className="tw-px-3 tw-py-2 tw-text-sm tw-text-content-muted">
                      No matching foreman areas.
                    </li>
                  ) : (
                    filteredAreas.map((area) => {
                      const selected = selectedForemanAreaIds.includes(area.id);
                      return (
                        <li key={area.id} className="tw-m-0">
                          <button
                            type="button"
                            role="option"
                            aria-selected={selected}
                            className={`tw-flex tw-min-h-11 tw-w-full tw-items-center tw-gap-2 tw-px-3 tw-text-left tw-text-sm ${
                              selected
                                ? 'tw-bg-ink tw-text-white'
                                : 'tw-bg-surface tw-text-content hover:tw-bg-surface-alt'
                            }`}
                            onClick={() => toggleForemanArea(area.id)}
                          >
                            <i
                              className={selected ? 'bi bi-check-circle-fill' : 'bi bi-circle'}
                              aria-hidden
                            />
                            {area.name}
                          </button>
                        </li>
                      );
                    })
                  )}
                </ul>
              </div>
            )}

            {toKind === 'service_area' && (
              <div className="tw-mt-3 tw-overflow-hidden tw-rounded-lg tw-border tw-border-line-input tw-bg-surface">
                {serviceAreasLoading ? (
                  <div className="tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-3 tw-text-sm tw-text-content-secondary">
                    <Spinner size="sm" />
                    Loading service areas…
                  </div>
                ) : serviceAreasError ? (
                  <p className="tw-mb-0 tw-px-3 tw-py-3 tw-text-sm tw-text-danger">{serviceAreasError}</p>
                ) : serviceAreas.length === 0 ? (
                  <p className="tw-mb-0 tw-px-3 tw-py-3 tw-text-sm tw-text-content-muted">
                    You have no service areas to send to.
                  </p>
                ) : (
                  <ul
                    role="listbox"
                    aria-label="Service areas"
                    className="tw-m-0 tw-max-h-48 tw-list-none tw-overflow-y-auto tw-p-0"
                  >
                    {serviceAreas.map((area) => {
                      const selected = selectedServiceAreaId === area.id;
                      const names = area.foremanAreaIds
                        .map((id) => foremanAreas.find((item) => item.id === id)?.name)
                        .filter(Boolean)
                        .join(', ');
                      return (
                        <li key={area.id} className="tw-m-0">
                          <button
                            type="button"
                            role="option"
                            aria-selected={selected}
                            className={`tw-flex tw-min-h-11 tw-w-full tw-flex-col tw-items-start tw-justify-center tw-px-3 tw-py-2 tw-text-left tw-text-sm ${
                              selected
                                ? 'tw-bg-ink tw-text-white'
                                : 'tw-bg-surface tw-text-content hover:tw-bg-surface-alt'
                            }`}
                            onClick={() => setSelectedServiceAreaId(area.id)}
                          >
                            <span>{area.name}</span>
                            {names && (
                              <span className={selected ? 'tw-text-white/80' : 'tw-text-content-muted'}>
                                {names}
                              </span>
                            )}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            )}

            {toKind === 'people' && (
              <div className="tw-mt-3 tw-space-y-3">
                <Form.Group controlId="message-board-people-area">
                  <Form.Label>Area</Form.Label>
                  <Form.Select
                    aria-label="Area"
                    value={peopleAreaId}
                    onChange={(e) => {
                      const next = Number(e.target.value);
                      setPeopleAreaId(Number.isFinite(next) ? next : '');
                    }}
                  >
                    {foremanAreas.map((area) => (
                      <option key={area.id} value={area.id}>
                        {area.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
                <div className="tw-overflow-hidden tw-rounded-lg tw-border tw-border-line-input tw-bg-surface">
                  <div className="tw-relative tw-border-b tw-border-line">
                    <i
                      className="bi bi-search tw-pointer-events-none tw-absolute tw-left-3 tw-top-1/2 tw--translate-y-1/2 tw-text-content-muted"
                      aria-hidden
                    />
                    <input
                      type="search"
                      value={peopleSearch}
                      onChange={(e) => setPeopleSearch(e.target.value)}
                      placeholder="Search people..."
                      className="tw-block tw-w-full tw-border-0 tw-bg-transparent tw-py-2.5 tw-pl-9 tw-pr-3 tw-text-sm tw-text-content placeholder:tw-text-content-muted focus:tw-outline-none"
                      aria-label="Search people"
                    />
                  </div>
                  {peopleLoading ? (
                    <div className="tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-3 tw-text-sm tw-text-content-secondary">
                      <Spinner size="sm" />
                      Loading people…
                    </div>
                  ) : peopleError ? (
                    <p className="tw-mb-0 tw-px-3 tw-py-3 tw-text-sm tw-text-danger">{peopleError}</p>
                  ) : (
                    <ul
                      role="listbox"
                      aria-label="People"
                      aria-multiselectable="true"
                      className="tw-m-0 tw-max-h-48 tw-list-none tw-overflow-y-auto tw-p-0"
                    >
                      {filteredPeople.length === 0 ? (
                        <li className="tw-px-3 tw-py-2 tw-text-sm tw-text-content-muted">
                          No people in this area.
                        </li>
                      ) : (
                        filteredPeople.map((person) => {
                          const selected = selectedPersonIds.includes(person.id);
                          return (
                            <li key={person.id} className="tw-m-0">
                              <button
                                type="button"
                                role="option"
                                aria-selected={selected}
                                className={`tw-flex tw-min-h-11 tw-w-full tw-items-center tw-gap-2 tw-px-3 tw-text-left tw-text-sm ${
                                  selected
                                    ? 'tw-bg-ink tw-text-white'
                                    : 'tw-bg-surface tw-text-content hover:tw-bg-surface-alt'
                                }`}
                                onClick={() =>
                                  setSelectedPersonIds((current) =>
                                    current.includes(person.id)
                                      ? current.filter((id) => id !== person.id)
                                      : [...current, person.id]
                                  )
                                }
                              >
                                <i
                                  className={selected ? 'bi bi-check-circle-fill' : 'bi bi-circle'}
                                  aria-hidden
                                />
                                {person.name}
                              </button>
                            </li>
                          );
                        })
                      )}
                    </ul>
                  )}
                </div>
                <p className="tw-mb-0 tw-text-sm tw-text-content-muted">
                  Only people in areas you can access are listed. The notification goes to the
                  people you select.
                </p>
              </div>
            )}

            {toKind === 'digital_sign' && (
              <p className="tw-mb-0 tw-mt-2 tw-text-sm tw-text-content-muted">
                Digital sign board is a placeholder. Delivery to signs is not available yet.
              </p>
            )}
          </div>

          <Form.Group className="tw-mb-3" controlId="message-board-subject">
            <Form.Label className="tw-sr-only">Subject</Form.Label>
            <Form.Control
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Subject — e.g. Winter tire check before Monday"
            />
          </Form.Group>

          <Form.Group className="tw-mb-3" controlId="message-board-message">
            <Form.Label className="tw-sr-only">Message</Form.Label>
            <Form.Control
              as="textarea"
              rows={5}
              value={message}
              onChange={(e) => {
                setMessage(e.target.value);
                if (messageError) setMessageError(null);
              }}
              placeholder="Write the message your crew will see on their phone..."
              isInvalid={!!messageError}
              required
            />
            {messageError && (
              <div className="tw-mt-1 tw-text-sm tw-text-danger">{messageError}</div>
            )}
          </Form.Group>

          <div
            role="group"
            aria-label="Message options"
            className="tw-mb-4 tw-flex tw-flex-wrap tw-gap-2"
          >
            <button
              type="button"
              className={optionChipClass(urgency === 'URGENT')}
              aria-pressed={urgency === 'URGENT'}
              onClick={() =>
                setUrgency((prev) => (prev === 'URGENT' ? 'NORMAL' : 'URGENT'))
              }
            >
              <i className="bi bi-exclamation-triangle-fill" aria-hidden />
              Urgent
            </button>
            <button
              type="button"
              className={optionChipClass(requireAck)}
              aria-pressed={requireAck}
              onClick={() => setRequireAck((prev) => !prev)}
              title="Acknowledgement is not stored yet — visual only for this release"
            >
              <i className="bi bi-check2" aria-hidden />
              Require acknowledgement
            </button>
            <button
              type="button"
              className={`${optionChipClass(false)} tw-opacity-60`}
              disabled
              title="Attachments are not available yet"
            >
              <i className="bi bi-paperclip" aria-hidden />
              Attach
            </button>
          </div>
        </div>

        <div className="tw-flex tw-flex-col tw-gap-3 tw-border-t tw-border-line tw-bg-surface tw-px-4 tw-py-3 sm:tw-flex-row sm:tw-items-center sm:tw-justify-between sm:tw-px-5">
          <p className="tw-mb-0 tw-text-sm tw-text-content-muted">
            {canSend
              ? `Goes to people in ${audienceLabel}`
              : toKind === 'digital_sign'
                ? 'Digital sign board is not available yet'
                : toKind === 'people'
                  ? selectedPersonIds.length === 0
                    ? 'Choose at least one person'
                    : `Goes to ${audienceLabel}`
                  : 'Choose an area to send'}
          </p>
          <Button
            type="submit"
            variant="dark"
            disabled={sendDisabled}
            className="tw-min-h-11 tw-rounded-full tw-bg-ink tw-px-4 tw-text-white hover:tw-bg-ink-soft"
          >
            {submitting ? (
              <>
                <Spinner size="sm" className="tw-me-2" />
                Sending…
              </>
            ) : (
              <>
                <i className="bi bi-send-fill tw-me-1.5" aria-hidden />
                Send notification
              </>
            )}
          </Button>
        </div>
      </form>

      <Modal
        show={showUrgentConfirm}
        onHide={() => !submitting && setShowUrgentConfirm(false)}
        size="sm"
        centered
      >
        <Modal.Header closeButton={!submitting}>
          <Modal.Title>Send urgent notification?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          This will mark the message as urgent for everyone in{' '}
          <strong>{confirmAreaLabel}</strong>. Continue?
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowUrgentConfirm(false)}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button variant="danger" onClick={() => void post()} disabled={submitting}>
            {submitting ? 'Sending…' : 'Send urgent'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default MessageBoardComposer;
